/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as ActionTypes from "constants/ActionTypes";
import gatewayFetch from "utilities/gatewayFetch";
import { mapFromServerFormat } from "utilities/ApiUtilities";
import { AppAction } from "types/AppAction";
import {
  AdobeId,
  CurrentUser,
  CurrentUserLoadingState,
} from "reducers/CurrentUserReducer";
import {
  SetCurrentUserAction,
  UserAuthFinishedAction,
  UserAuthStartedAction,
} from "types/Actions";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo } from "react";
import { AppPath } from "components/AppPath";
import getValidAccessToken, {
  getAdobeIdFromAccessToken,
} from "utilities/getValidAccessToken";
import { behanceApi } from "api/BehanceApi";
import Analytics from "constants/Analytics";
import { noticeError } from "utilities/noticeError";
import { signOutAndIncrement } from "utilities/SignInLoopUtilities";
import { addToast } from "actions/ToastMessageActions";
import { identifyCustomer } from "api/CustomerPersonalization";
import { useHistoryReplace } from "utilities/NavigationUtilities";

export interface CurrentUserHookResult {
  // currentUser will be null if anonymousAllowedCurrentUser is null or
  // anonymousAllowedCurrentUser.isAnonymous is true
  currentUser: CurrentUser | null;
  anonymousAllowedCurrentUser: CurrentUser | null;

  fetchCurrentUser: () => Promise<CurrentUser | null>;
  refreshCurrentUser: () => Promise<CurrentUser | null>;
  isCurrentUserLoaded: boolean; // Deprecated - use currentUserLoadingState
  currentUserLoadingState: CurrentUserLoadingState;
}

export const isCurrentUserTokenValid = async (): Promise<boolean> => {
  if (!window.adobeIMS) {
    return false;
  }

  try {
    return await window.adobeIMS.validateToken();
  } catch {
    return false;
  }
};

export const useBlockUserBasedOnProfileCountryCode = (
  currentUser: CurrentUser | null
): void => {
  const localizedReplace = useHistoryReplace();

  useEffect(() => {
    if (
      currentUser?.hasBlockedCountryCodeForUserProfile &&
      !window.location.pathname.endsWith(AppPath.BLOCKED_REGION)
    ) {
      localizedReplace(AppPath.BLOCKED_REGION);
    }
  }, [currentUser, localizedReplace]);
};

export const useCurrentUser = (): CurrentUserHookResult => {
  const currentUser = useSelector((state) => state.currentUser);
  const currentUserLoaded = useSelector((state) => state.currentUserLoaded);
  const dispatch = useDispatch();
  const fetchCurrentUser = useCallback(() => {
    if (navigator.userAgent === "ReactSnap") {
      return Promise.resolve(null);
    }
    return dispatch(findOrCreateSession());
  }, [dispatch]);
  const refreshCurrentUser = useCallback(async () => {
    await dispatch(setCurrentUser(null));
    if (navigator.userAgent === "ReactSnap") {
      return Promise.resolve(null);
    }
    return await dispatch(findOrCreateSession());
  }, [dispatch]);

  const isCurrentUserLoaded =
    currentUserLoaded === CurrentUserLoadingState.LOADED;

  useEffect(() => {
    Analytics.instance.setCurrentUser(currentUser);

    if (currentUser) {
      newrelic.setCustomAttribute("userId", currentUser.adobeId);
      newrelic.setCustomAttribute(
        "isAnonymousUser",
        `${currentUser.isAnonymous}`
      );
    }
  }, [currentUser]);

  useBlockUserBasedOnProfileCountryCode(currentUser);

  const nonAnonymousUser = useMemo(() => {
    if (!currentUser || currentUser.isAnonymous) {
      return null;
    }

    return currentUser;
  }, [currentUser]);

  return {
    currentUser: nonAnonymousUser,
    anonymousAllowedCurrentUser: currentUser,
    currentUserLoadingState: currentUserLoaded,
    isCurrentUserLoaded,
    fetchCurrentUser,
    refreshCurrentUser,
  };
};

function isStringEmpty(value: string | undefined): boolean {
  return !value || value.trim() === "";
}

export const createBehanceProfile = (
  imsUserProfile: AdobeIMSProfile
): AppAction<Promise<void>> => {
  return async (dispatch): Promise<void> => {
    if (
      imsUserProfile.account_type === "guest" ||
      imsUserProfile.account_type === "null"
    ) {
      return;
    }
    const { data } = await dispatch(
      behanceApi.endpoints.getProfileByAdobeId.initiate(imsUserProfile.userId)
    );
    if (
      !data ||
      isStringEmpty(data.firstName) ||
      isStringEmpty(data.displayName)
    ) {
      dispatch(
        behanceApi.endpoints.createProfile.initiate({
          adobeId: imsUserProfile.userId,
          firstName: imsUserProfile.first_name,
          lastName: imsUserProfile.last_name,
        })
      );
    }
  };
};

export const findOrCreateSession = (): AppAction<
  Promise<CurrentUser | null>
> => {
  return async (dispatch, getState): Promise<CurrentUser | null> => {
    const { currentUserLoaded } = getState();
    if (currentUserLoaded !== CurrentUserLoadingState.NOT_LOADED) {
      return getState().currentUser;
    }

    await dispatch(userAuthStarted());

    try {
      const imsUserProfileOrError: Result<
        AdobeIMSProfile,
        { error: unknown; errorType?: unknown }
      > = await window.imsUserProfileReady
        .then((profile) => ({
          type: "resolved" as const,
          value: profile,
        }))
        .catch((e) => ({ type: "error", error: e }));

      if (imsUserProfileOrError.type === "error") {
        const { error, errorType } = imsUserProfileOrError.error;
        if (error === "Invalid token") {
          // if the token is not valid immediately after refreshing during page initialization,
          // something funky is going on. Probably env issues between stage/prod/dev. Just sign out and let
          // a fresh sign in reset things
          newrelic.addPageAction("INVALID_TOKEN_AFTER_REFRESH");
          signOutAndIncrement();
        } else {
          newrelic.noticeError("Error thrown by imsUserProfileReady", {
            error: `${error}`,
            errorType: `${errorType}`,
          });
          await dispatch(userAuthFailed());
        }
        return null;
      }

      const imsUserProfile = imsUserProfileOrError.value;

      if (imsUserProfile === null) {
        newrelic.noticeError("no profile returns from imsUserProfileReady");
        dispatch(userAuthFinished());
        return null;
      }

      if (imsUserProfile.account_type === "guest") {
        const adobeId = await getAdobeIdFromAccessToken(
          adobeIMS?.getAccessToken() ?? undefined
        );
        if (!adobeId) {
          throw "No adobe id found from guest token";
        }
        const anonymousUser = buildAnonymousCurrentUser(adobeId);
        dispatch(setCurrentUser(anonymousUser));
        return anonymousUser;
      }

      if (
        imsUserProfile.account_type === "null" ||
        !window.adobeIMS?.isSignedInUser()
      ) {
        await dispatch(userAuthFinished());
        return null;
      }

      const { userId: adobeId } = imsUserProfile;
      if (!adobeId) {
        await dispatch(userAuthFinished());
        return null;
      }

      const response = await gatewayFetch(
        "/api/v1/user",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
        { reportErrors: false, reauthenticateOn401: false }
      );

      if (!response.ok) {
        noticeError("User fetch failed", {
          userId: adobeId,
          responseStatus: response.status,
        });
        if (response.status === 401 && adobeIMS?.isSignedInUser()) {
          noticeError("Signing out a signed in user because auth failed");
          signOutAndIncrement();
        } else {
          await dispatch(userAuthFailed());
        }

        return null;
      }

      const json = await response.json();
      const currentUser: CurrentUser = mapFromServerFormat(
        json.current_user
      ) as unknown as CurrentUser;
      await dispatch(setCurrentUser(currentUser));

      identifyCustomer(imsUserProfile.email, adobeId);

      dispatch(createBehanceProfile(imsUserProfile));

      return currentUser;
    } catch (e) {
      if (e instanceof Error) {
        noticeError(e);
      } else {
        if (e.error !== "Initialization error") {
          noticeError("IMS error in findOrCreateSession", e);
          if (adobeIMS?.isSignedInUser()) {
            noticeError("Signing out a signed in user because auth failed");
            signOutAndIncrement();
          } else {
            await dispatch(userAuthFailed());
          }
        } else {
          // IMS lib throws an error after the user signs out which we should ignore
          await dispatch(userAuthFinished());
        }
      }
      return null;
    }
  };
};

const userAuthFinished = (): UserAuthFinishedAction => {
  return {
    type: ActionTypes.USER_AUTH_FINISHED,
  };
};

function userAuthFailed(): AppAction<Promise<void>> {
  return async (dispatch): Promise<void> => {
    newrelic.noticeError("User shown connection failed toast", {
      online: String(navigator.onLine),
    });

    dispatch(addToast({ message: I18n.t("login.failed.toast") }));

    dispatch({
      type: ActionTypes.USER_AUTH_FAILED,
    });
  };
}

const userAuthStarted = (): UserAuthStartedAction => {
  return {
    type: ActionTypes.USER_AUTH_STARTED,
  };
};

export function setCurrentUser(
  currentUser: null | CurrentUser
): SetCurrentUserAction {
  return {
    type: ActionTypes.SET_CURRENT_USER,
    currentUser,
  };
}

export function signOutUser(): AppAction<Promise<void>> {
  return async (dispatch): Promise<void> => {
    window.adobeIMS?.signOut();
    dispatch(setCurrentUser(null));
  };
}

export async function acquireAccessToken(): Promise<string | null> {
  const accessTokenInfo = await getValidAccessToken();

  if (!accessTokenInfo) {
    console.log("sign in acquireAccessToken");
    await window.adobeIMS?.signIn();
    return null;
  } else {
    return accessTokenInfo.token;
  }
}

type Result<T, E> =
  | {
      type: "resolved";
      value: T;
    }
  | {
      type: "error";
      error: E;
    };

const buildAnonymousCurrentUser = (adobeId: AdobeId): CurrentUser => {
  return {
    adobeId: adobeId,
    canCreateAnonymousProjectAccessKeys: false,
    studioBackendExportEnabled: false,
    studioBackendExportMp3Enabled: false,
    studioDownloadOriginalRecordingTracksEnabled: false,
    studioRecordingSessionImportEnabled: false,
    enhanceSpeechLimits: {
      fileSizeLimitBytes: 0,
      videoFileSizeLimitBytes: 0,
      dailyDurationLimitSeconds: 0,
      fileDurationLimitSeconds: 0,
      pollingTimeoutSeconds: 0,
    },
    hasAdminAccess: false,
    hasAllBrowserStudioAccess: false,
    hasBlockedCountryCodeForUserProfile: false,
    hasEnhanceSpeechAccess: false,
    hasFreeStudio: false,
    hasPremiumAccess: false,
    isAnonymous: true,
    studioConvertedAudioUploadEnabled: false,
    studioVideoUploadEnabled: false,
    editSpeakerNameEnabled: false,
    hasStreamingStudioPlayerAccess: false,
    postEnhanceStudioNudgeEnabled: false,
    studioHeaderUpgradeButtonEnabled: false,
    studioTranscriptionSpeakerDiarizationEnabled: false,
    esqaTrackMutingEnabled: false,
    esqaIndividualTrackDownloadEnabled: false,
    studioRecordingV2Enabled: false,
    studioRecordingV2WebrtcTestEnabled: false,
    sl4wEsqaEnabled: false,
    sl4wStudioEnabled: false,
    submitFeedbackEnabled: false,
    fireflyProTrialToPaidEnabled: false,
  };
};
